import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Calendar as CalendarIcon, Users, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Meetings() {
  const [meetings, setMeetings] = useState([]);
  const [users, setUsers] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newMeeting, setNewMeeting] = useState({
    title: '',
    date: '',
    notes: '',
    attendees: []
  });

  useEffect(() => {
    fetchMeetings();
    fetchUsers();
  }, []);

  const fetchMeetings = async () => {
    try {
      const response = await axios.get(`${API}/meetings`);
      setMeetings(response.data);
    } catch (error) {
      toast.error('Failed to fetch meetings');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data.filter(u => u.status === 'active'));
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleCreateMeeting = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/meetings`, newMeeting);
      toast.success('Meeting created successfully');
      setShowCreateDialog(false);
      setNewMeeting({ title: '', date: '', notes: '', attendees: [] });
      fetchMeetings();
    } catch (error) {
      toast.error('Failed to create meeting: ' + (error.response?.data?.detail || error.message));
    }
  };

  const toggleAttendee = (userId) => {
    setNewMeeting(prev => ({
      ...prev,
      attendees: prev.attendees.includes(userId)
        ? prev.attendees.filter(id => id !== userId)
        : [...prev.attendees, userId]
    }));
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isPastMeeting = (dateStr) => {
    return new Date(dateStr) < new Date();
  };

  return (
    <div className="space-y-6 fade-in" data-testid="meetings-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Meetings</h1>
          <p className="text-gray-600">Manage meeting records and actions</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-blue-500 to-green-500" data-testid="create-meeting-button">
              <Plus className="w-4 h-4 mr-2" />
              Create Meeting
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Meeting</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateMeeting} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Meeting Title *</Label>
                <Input
                  id="title"
                  value={newMeeting.title}
                  onChange={(e) => setNewMeeting({...newMeeting, title: e.target.value})}
                  placeholder="e.g., HLRA Committee Meeting - January 2026"
                  required
                  data-testid="meeting-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date">Date & Time *</Label>
                <Input
                  id="date"
                  type="datetime-local"
                  value={newMeeting.date}
                  onChange={(e) => setNewMeeting({...newMeeting, date: e.target.value})}
                  required
                  data-testid="meeting-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Meeting Notes / Agenda</Label>
                <Textarea
                  id="notes"
                  value={newMeeting.notes}
                  onChange={(e) => setNewMeeting({...newMeeting, notes: e.target.value})}
                  placeholder="Enter agenda items or meeting notes..."
                  rows={4}
                  data-testid="meeting-notes-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Attendees ({newMeeting.attendees.length} selected)</Label>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-md p-3">
                  {users.map((user) => (
                    <label 
                      key={user.id} 
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${newMeeting.attendees.includes(user.id) ? 'bg-blue-50' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={newMeeting.attendees.includes(user.id)}
                        onChange={() => toggleAttendee(user.id)}
                        className="rounded"
                      />
                      <span className="text-sm">{user.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-gradient-to-r from-blue-500 to-green-500" data-testid="submit-meeting-button">
                  Create Meeting
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Meetings ({meetings.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : meetings.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className="text-lg">No meetings yet</p>
              <p className="text-sm mt-2">Create your first meeting to start tracking actions</p>
            </div>
          ) : (
            <div className="space-y-3">
              {meetings.map((meeting) => {
                const isPast = isPastMeeting(meeting.date);
                const attendeeCount = meeting.attendees?.length || 0;
                
                return (
                  <Link
                    key={meeting.id}
                    to={`/meetings/${meeting.id}`}
                    className={`block p-4 border rounded-lg hover:bg-gray-50 transition card-hover ${isPast ? 'border-gray-200 bg-gray-50' : 'border-blue-200'}`}
                    data-testid={`meeting-${meeting.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {isPast ? (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">Past</span>
                          ) : (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">Upcoming</span>
                          )}
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">{meeting.title}</h3>
                        {meeting.notes && (
                          <p className="text-sm text-gray-600 mb-2 line-clamp-2">{meeting.notes}</p>
                        )}
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          <span className="flex items-center">
                            <CalendarIcon className="w-3 h-3 mr-1" />
                            {formatDate(meeting.date)}
                          </span>
                          {attendeeCount > 0 && (
                            <span className="flex items-center">
                              <Users className="w-3 h-3 mr-1" />
                              {attendeeCount} attendee{attendeeCount !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center text-gray-400">
                        <FileText className="w-5 h-5" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
